import { api } from "@/lib/api";

let streamP = null;

// One shared mic stream per session: avoids re-prompting and start-up lag between items
export const getMicStream = () => {
  if (streamP) return streamP;
  if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(Object.assign(new Error("unsupported"), { name: "NotSupportedError" }));
  streamP = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    .then((s) => { s.getTracks().forEach((t) => { t.onended = () => { streamP = null; }; }); return s; })
    .catch((e) => { streamP = null; throw e; });
  return streamP;
};

export const releaseMic = () => {
  if (!streamP) return;
  streamP.then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {});
  streamP = null;
};

export const micErrorKind = (e) => (e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "denied" : e?.name === "NotFoundError" ? "nodevice" : "error");

const pickMime = () => {
  if (!window.MediaRecorder) return "";
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
};

// Starts recording on the shared stream; returns a controller with live level and stop()
export const startRecording = async (onLevel) => {
  const stream = await getMicStream();
  const mime = pickMime();
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const resume = () => ctx.state === "suspended" && ctx.resume().catch(() => {});
  let wasSuspended = ctx.state !== "running";
  ctx.onstatechange = () => { if (ctx.state !== "running") wasSuspended = true; };
  resume();
  window.addEventListener("pointerdown", resume);
  window.addEventListener("keydown", resume);
  const an = ctx.createAnalyser(); an.fftSize = 1024;
  const src = ctx.createMediaStreamSource(stream); src.connect(an);
  const buf = new Float32Array(an.fftSize);
  let peak = 0, voiced = 0, raf;
  const tick = () => {
    an.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    peak = Math.max(peak, rms);
    if (rms > 0.02) voiced++;
    onLevel?.(Math.min(100, rms * 600));
    raf = requestAnimationFrame(tick);
  };
  tick();
  rec.start(250);
  let stopped = null;
  return {
    stop() {
      if (stopped) return stopped;
      stopped = new Promise((res) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          // a suspended analyser can't measure voice, so never drop the recording in that case
          const measured = !wasSuspended && ctx.state === "running";
          window.removeEventListener("pointerdown", resume); window.removeEventListener("keydown", resume);
          cancelAnimationFrame(raf); src.disconnect(); ctx.close().catch(() => {});
          res({ blob: chunks.length ? new Blob(chunks, { type: rec.mimeType || mime || "audio/webm" }) : null, peak, voiced: measured ? voiced : Infinity });
        };
        if (rec.state === "inactive") return finish();
        rec.addEventListener("stop", finish, { once: true });
        setTimeout(finish, 3000);
        try { rec.requestData(); } catch { /* noop */ }
        rec.stop();
      });
      return stopped;
    },
  };
};

const extFor = (type) => (type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm");

// Sends audio to the backend Whisper endpoint; silent recordings skip the call
export const transcribe = async ({ blob, voiced }) => {
  if (!blob || blob.size < 1200 || voiced < 3) return "";
  const fd = new FormData();
  fd.append("file", blob, `answer.${extFor(blob.type)}`);
  for (let attempt = 0; attempt < 2; attempt++) {
    try { const { data } = await api.post("/stt", fd, { timeout: 45000 }); return data.text || ""; }
    catch (e) { if (attempt || e?.response?.status === 413) throw e; }
  }
  return "";
};
